import { TrendingUp, Users, Building, ShieldCheck } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "../components/shared/PageHeader";
import { CTASection } from "../components/shared/CTASection";
import { IMAGES } from "../data/content";

const DRIVERS = [
  { icon: Users, title: "Demand growth", desc: "A rising middle class and expanding connectivity are driving sustained passenger and cargo demand across the continent." },
  { icon: Building, title: "Infrastructure investment", desc: "New and upgraded airports, terminals and MRO capacity are reshaping the operating landscape." },
  { icon: TrendingUp, title: "Market liberalization", desc: "Initiatives such as the Single African Air Transport Market are opening cross-border opportunity." },
  { icon: ShieldCheck, title: "Safety maturity", desc: "Operators and regulators investing in safety and compliance maturity unlock partnerships and financing." },
];

export default function Africa() {
  return (
    <div data-testid="africa-page">
      <PageHeader
        testId="africa-header"
        eyebrow="Africa Aviation Growth Focus"
        title="A dedicated lens on the world's most promising aviation growth region."
        subtitle="We help operators, investors and public-sector bodies capture Africa's structural growth opportunity — built on safe, auditable foundations."
        image={IMAGES.africa}
      />

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">The opportunity</span>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl text-balance">
                Growth rewards those who build on solid foundations.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
                Africa is entering a structural growth phase for aviation. The organizations that win
                will treat safety, compliance and operational maturity as growth enablers — not cost
                centers. We combine operational improvement with the documentation and compliance
                foundations that unlock expansion, partnerships and financing.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {DRIVERS.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-white p-8 transition-transform hover:-translate-y-1 hover:shadow-xl">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand">
                    <d.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-bold text-navy">{d.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy text-white">
        <img src={IMAGES.africa} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/50" />
        <div className="grain relative mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
          <Reveal>
            <h2 className="max-w-3xl font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl text-balance">
              How we support organizations expanding in Africa
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {["Safety & compliance foundations for new routes", "Operational readiness and SOP standardization", "Documentation for partnerships and financing", "Security posture for expanding footprints", "AI readiness for modern operations", "Government capability positioning"].map((t) => (
                <div key={t} className="rounded-xl border border-white/10 bg-white/5 p-5 text-sm font-medium text-white/85">{t}</div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
